import { KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IChipsSearchFilterSection,
  ParamsKeyValuePair,
} from '@el-chip-input-search';
import {
  DataListTableChipDataElement,
  DataListTableDataElement,
  DataListTableElement,
  DLT_DATA_TYPES,
} from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subject, Subscription, takeUntil } from 'rxjs';

import {
  ATTENDANCE_STATUS,
  DB_COLLECTION_NAMES,
  MODULES,
  PERMISSION_ACTIONS,
  USER_TYPES,
} from '@shared/constants';
import {
  CategoryList,
  IAttendance,
  IIdentity,
  IIdentityResponse,
  IReferenceCategoryResponse,
  LocationList,
  ModalityList,
  TypeList,
} from '@shared/interfaces';
import { getModulePermission } from '@shared/utils';

import { CISService } from '../../services/cis.service';
import { DateTimeFormatService } from '../../services/date-time-format.service';
import { FilesService } from '../../services/files.service';
import { FlowControlService } from '../../services/flow-control.service';
import {
  ICreateData,
  ISpeedDialButtons,
  PermissionFiltrationService,
} from '../../services/permission-filtration.service';
import {
  LoggedUserService,
  UserInfoResponseDTO,
} from '../auth/services/logged-user.service';
import { IdentitiesInternalService } from '../identities/services/identities.internal.service';
import { IdentitiesService } from '../identities/services/identities.service';
import { CurrenciesResponseDTO } from '../setup/currencies/services/currencies.service';
import { IActionButton } from '../shared/components/action-button/action-button.component';

import { AttendanceComponentCISService } from './data/attendance-cis-filters';
import { AttendanceService } from './services/attendance.service';

const SPEED_DIAL = {
  UPLOAD_BULK: 'upload_bulk',
  CONFIGURATIONS: 'configurations',
};
@Component({
  selector: 'app-attendance',
  templateUrl: './attendance.component.html',
  styleUrls: ['./attendance.component.scss'],
})
export class AttendanceComponent implements OnInit, OnDestroy {
  isListLoading = true;
  loadingShowMore = false;

  // CIS
  start = 0;
  size = 10;
  totalCount = 0;
  category: KeyValue<string, string>[] = [];
  type: KeyValue<string, string>[] = [];
  modality: KeyValue<string, string>[] = [];

  filteredActionButtons: IActionButton[] = [];
  allActionButtons: ISpeedDialButtons[] = [];
  currenciesList: CurrenciesResponseDTO[] = [];
  statusDropDown: KeyValue<string, string>[] = [];

  categoryList: CategoryList[] = [];
  typeList: TypeList[] = [];
  modalityList: ModalityList[] = [];
  locationList: LocationList[] = [];
  referenceCategories: IReferenceCategoryResponse[];

  attendanceListArray: DataListTableElement[] = [];
  attendanceRecords: any[] = [];

  salutation: string;
  profileidentity: IIdentityResponse;
  loggedUser: UserInfoResponseDTO;
  isAdmin = false;

  chipsData: IChipsSearchFilterSection[] = [];
  statusDropdown: ParamsKeyValuePair[] = [];

  allPossibleKeys = [];
  allIdentities: { [key: string]: IIdentity } = {};

  private readonly onDestroy$ = new Subject<void>();
  private subscriptions = new Subscription();

  constructor(
    route: ActivatedRoute,
    private router: Router,
    private cisService: CISService,
    flowControlService: FlowControlService,
    private filesService: FilesService,
    private identitiesService: IdentitiesService,
    private identityInternalService: IdentitiesInternalService,
    private dateTimeFormatService: DateTimeFormatService,
    private permissionFiltrationService: PermissionFiltrationService,
    private attendanceService: AttendanceService,
    private attendanceComponentCISService: AttendanceComponentCISService,
    private loggedUserService: LoggedUserService,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef
  ) {
    flowControlService.updateRouteInfo(
      router.url.split('?')[0],
      route.snapshot.data
    );
    this.allPossibleKeys = [
      'status',
      'source',
      'location',
      'created_date_from',
      'created_date_to',
    ];
  }

  async ngOnInit(): Promise<void> {
    this.searchAttendance();
    this.initCIS();
    this.filterSpeedDialButtons();
    this.isAnAdmin();
  }

  async isAnAdmin() {
    this.loggedUserService.dataStore
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        if (data.user_type == USER_TYPES.EL_ADMIN) {
          this.isAdmin = true;
        }
      });
  }

  initCIS(): void {
    const { chipsData } =
      this.attendanceComponentCISService.getAllCISChipsList();
    this.cisService.initializesCIS(
      this.allPossibleKeys,
      chipsData,
      this.onDestroy$.asObservable(),
      (search?: boolean) => this.handleSearchButtonClickEvent(!search),
      DB_COLLECTION_NAMES.ATTENDANCE
    );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    this.subscriptions.unsubscribe();
  }

  filterSpeedDialButtons() {
    this.allActionButtons = [
      {
        data: {
          action: SPEED_DIAL.UPLOAD_BULK,
          icon: 'add',
          tooltip: 'Upload Bulk Attendance',
        },
        allowedPermissions: getModulePermission(MODULES.ATTENDANCE, [
          PERMISSION_ACTIONS.WRITE,
        ]),
      },
      {
        data: {
          action: SPEED_DIAL.CONFIGURATIONS,
          icon: 'settings',
          tooltip: 'Attendance Configurations',
        },
        allowedPermissions: getModulePermission(MODULES.ATTENDANCE, [
          PERMISSION_ACTIONS.WRITE,
        ]),
      },
    ];
    this.filteredActionButtons =
      this.permissionFiltrationService.filterSpeedDialButtons(
        this.allActionButtons
      );
  }

  public handleSearchButtonClickEvent(search: boolean): void {
    this.start = 0;
    this.size = 10;
    if (search === false) {
      this.isListLoading = false;
      return;
    }
    this.searchAttendance();
  }

  onPageSizeChange(size: number): void {
    if (size) {
      this.size = size;
    }
  }

  showMore(e: boolean) {
    if (e) {
      this.loadingShowMore = true;
      this.start = this.attendanceListArray.length;
      this.searchAttendance(true);
    }
  }

  searchAttendance(replaceUrl = false) {
    if (!this.loadingShowMore) {
      this.isListLoading = true;
    }

    const { httpParams, onFinishSearch } = this.cisService.getSearchParams(
      this.start,
      this.size
    );

    let usedCISChipsList = httpParams.keys();

    usedCISChipsList = usedCISChipsList.filter(
      (chip) => chip !== 'is_search' && chip !== 'start' && chip !== 'size'
    );

    this.cisService.setFrequentlyUsedChipsFor(
      DB_COLLECTION_NAMES.ATTENDANCE,
      usedCISChipsList
    );
    this.attendanceService
      .getAllAttendance(httpParams)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (res) => {
          this.totalCount = res?.count;
          this.attendanceListArray =
            this.start > 0 ? this.attendanceListArray : [];

          const promises = [];
          res.data.forEach(async (attendance) => {
            const index = this.attendanceListArray.length;
            const dataElements = this.get_data_elements(attendance);

            this.identityInternalService
              .getSingleIdentityFromConnection(attendance.employee_identity)
              .subscribe((response) => {
                this.profileidentity = response.data;
                promises.push(
                  this.generateAvatars(index, this.profileidentity)
                );
              });
            this.attendanceListArray.push({
              loading: false,
              expanded: false,
              data: dataElements,
              icon: 'person',
              index: this.attendanceListArray.length,
              originalData: attendance,
              actions: [],
            });
          });
          this.isListLoading = false;
          this.loadingShowMore = false;
          // @@@@@@@@@@@@@@@@ below method
          onFinishSearch({
            replaceUrl,
          });
          // @@@@@@@@@@@@@@@@ comment above method
        },
        error: () => {
          this.isListLoading = false;
          this.loadingShowMore = false;
        },
      });
    this.isListLoading = true;
  }
  async generateAvatars(
    index: number,
    identity: IIdentityResponse
  ): Promise<void> {
    const imageData = await this.filesService.getProfilePictureOneTime(
      identity
    );
    if (this.attendanceListArray && this.attendanceListArray[index]) {
      this.attendanceListArray[index].avatar = imageData;
    } else {
      console.error(`Invalid index ${index} for avatar assignment`);
    }
  }

  formatTime(date: Date): string {
    if (!date) return '-';
    return moment(date).format('hh:mm A');
  }

  private get_data_elements(
    attendance: IAttendance
  ): DataListTableDataElement[] {
    const dataElements: ICreateData[] = [];

    const clockingDate = new Date(
      attendance?.clockedIn_time
    ).toLocaleDateString('en-GB');
    const createdOnTime = new Date(attendance?.created_on).toLocaleTimeString(
      'en-US',
      {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }
    );

    const combinedDateTime = `${clockingDate} (${createdOnTime})`;

    dataElements.push(
      {
        data: {
          key: this.translate.instant('attendance.dlt.created-by'),
          value: `${attendance.full_name} (${attendance.employee_id})`,
        },
      },
      {
        data: {
          key: this.translate.instant('attendance.dlt.created-date'),
          value: combinedDateTime,
        },
      },
      {
        data: {
          key: this.translate.instant('attendance.dlt.clocked-in-time'),
          value: this.formatTime(attendance?.clockedIn_time),
        },
      },
      {
        data: {
          key: this.translate.instant('attendance.dlt.clocked-out-time'),
          value: this.formatTime(attendance?.clockedOut_time),
        },
      },
      {
        data: {
          key: this.translate.instant('attendance.dlt.location'),
          value: attendance?.place,
        },
      },
      {
        data: {
          key: this.translate.instant('attendance.dlt.duration'),
          value: attendance?.hours_worked || '-',
        },
      },
      {
        data: {
          ...this.setAttendanceChip(attendance),
        },
      },
      {
        data: {
          key: this.translate.instant('attendance.dlt.source'),
          value: attendance?.source,
        },
      }
    );

    return this.permissionFiltrationService.getData(dataElements);
  }

  private setAttendanceChip(attendanceStatus: IAttendance) {
    const statusDisplayDetails: DataListTableChipDataElement = {
      key: this.translate.instant('vacancies.dlt.status'),
      value: attendanceStatus.status,
      type: DLT_DATA_TYPES.CHIP,
      chipBackgroundColor: '',
      chipColor: '',
    };
    switch (attendanceStatus.status) {
      case ATTENDANCE_STATUS.VERIFIED:
        statusDisplayDetails.value = this.translate.instant(
          'attendance.dlt.status.verified'
        );
        statusDisplayDetails.chipBackgroundColor = 'green';
        statusDisplayDetails.chipColor = 'white';
        break;

      case ATTENDANCE_STATUS.UNVERIFIED:
        statusDisplayDetails.value = this.translate.instant(
          'attendance.dlt.status.unverified'
        );
        statusDisplayDetails.chipBackgroundColor = '#888888';
        statusDisplayDetails.chipColor = 'white';
        break;
      default:
        break;
    }
    return statusDisplayDetails;
  }

  onClickAction(action: string) {
    if (action === SPEED_DIAL.UPLOAD_BULK) {
      this.router.navigate(['/app/attendance/hr-bulk-upload/upload']);
    } else if (action === SPEED_DIAL.CONFIGURATIONS) {
      this.router.navigate(['/app/attendance/attendance-configuration']);
    }
  }
}
